import * as XLSX from 'xlsx-js-style'

/**
 *
 * @param component Components that need to be registered
 * @param alias component alias
 * @returns any
 */
export const withInstall = <T>(component: T, alias?: string) => {
  const comp = component as any
  comp.install = (app: any) => {
    app.component(comp.name || comp.displayName, component)
    if (alias) {
      app.config.globalProperties[alias] = component
    }
  }
  return component as T & Plugin
}

/**
 * @param str Need to turn down the hump string
 * @returns Strike underline
 */
export const humpToUnderline = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase()
}

/**
 * @param str Need to turn the drooped string of the hump peak
 * @returns String hump
 */
export const underlineToHump = (str: string): string => {
  if (!str) return ''
  return str.replace(/\-(\w)/g, (_, letter: string) => {
    return letter.toUpperCase()
  })
}

/**
 * Catalum turning
 */
export const humpToDash = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase()
}

export const setCssVar = (prop: string, val: any, dom = document.documentElement) => {
  dom.style.setProperty(prop, val)
}

export const getCssVar = (prop: string, dom = document.documentElement) => {
  return getComputedStyle(dom).getPropertyValue(prop)
}

/**
 * Find a certain bid in the array object
 * @param {Array} ary Find array
 * @param {Functon} fn Method of judgment
 */
// eslint-disable-next-line
export const findIndex = <T = Recordable>(ary: Array<T>, fn: Fn): number => {
  if (ary.findIndex) {
    return ary.findIndex(fn)
  }
  let index = -1
  ary.some((item: T, i: number, ary: Array<T>) => {
    const ret: T = fn(item, i, ary)
    if (ret) {
      index = i
      return ret
    }
  })
  return index
}

export const trim = (str: string) => {
  return str.replace(/(^\s*)|(\s*$)/g, '')
}

/**
 * @param {Date | number | string} time Time to be converted
 * @param {String} fmt Formats that need to be converted, such as Yyyy-MM-DD, YYYY-MM-DD HH: MM: SS
 */
export function formatTime(time: Date | number | string, fmt: string) {
  if (!time) return ''
  else {
    const date = new Date(time)
    const o = {
      'M+': date.getMonth() + 1,
      'd+': date.getDate(),
      'H+': date.getHours(),
      'm+': date.getMinutes(),
      's+': date.getSeconds(),
      'q+': Math.floor((date.getMonth() + 3) / 3),
      S: date.getMilliseconds()
    }
    if (/(y+)/.test(fmt)) {
      fmt = fmt.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length))
    }
    for (const k in o) {
      if (new RegExp('(' + k + ')').test(fmt)) {
        fmt = fmt.replace(
          RegExp.$1,
          RegExp.$1.length === 1 ? o[k] : ('00' + o[k]).substr(('' + o[k]).length)
        )
      }
    }
    return fmt
  }
}

/**
 * Generate random string
 */
export function toAnyString() {
  const str: string = 'xxxxx-xxxxx-4xxxx-yxxxx-xxxxx'.replace(/[xy]/g, (c: string) => {
    const r: number = (Math.random() * 16) | 0
    const v: number = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString()
  })
  return str
}

/**
 * First letters
 */
export function firstUpperCase(str: string) {
  return str.toLowerCase().replace(/( |^)[a-z]/g, (L) => L.toUpperCase())
}

/**
 * Turn the object to FormData
 */
export function objToFormData(obj: Recordable) {
  const formData = new FormData()
  Object.keys(obj).forEach((key) => {
    formData.append(key, obj[key])
  })
  return formData
}

/**
 * 주어진 배열의 객체에서 특정 속성 값을 추출합니다.
 * @param {Array} arr - 객체 배열. 중첩된 children이 포함될 수 있음.
 * @param {String} key - 객체에서 추출하고자 하는 특정 속성
 * @returns {Array} 특정 속성 값들의 배열
 */
export const extractProps = (arr, key) => {
  return arr.reduce((acc, arr) => {
    if (arr[key]) acc.push(arr[key])
    if (arr.children) acc.push(...extractProps(arr.children, key))
    return acc
  }, [])
}

export const convertToCamelCase = (str) => {
  return str.replace(/(?:^|-)([a-z])/g, (_, c) => c.toUpperCase())
}

export const filterObject = (obj) => {
  const filteredEntries = Object.entries(obj).filter(
    ([_, value]) => value !== 'default' && value !== '' && value !== null && value !== undefined
  )
  return Object.fromEntries(filteredEntries)
}

export const createButtonBindings = (permissions) => {
  const buttonBindings =
    permissions?.reduce((obj, permission) => {
      const key = `show${convertToCamelCase(permission)}`
      obj[key] = true
      return obj
    }, {}) ?? {}
  return buttonBindings
}

/**
 * 엑셀 파일을 읽어 JSON 데이터로 변환합니다.
 * @param {File} file - 읽을 엑셀 파일
 * @returns {Promise<any[]>} 변환된 JSON 데이터
 */
export const readExcelFile = (file: File, isEmptyDefval?: boolean): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    // 파일 읽기 완료 시 호출되는 이벤트 핸들러
    reader.onload = (e) => {
      const target = e.target as FileReader
      if (target.result) {
        // 파일 데이터를 Uint8Array로 변환
        const data = new Uint8Array(target.result as ArrayBuffer)
        // 엑셀 파일을 읽어 워크북 생성
        const workbook = XLSX.read(data, { type: 'array' })
        // 첫 번째 시트의 이름을 가져옴
        const sheetName = workbook.SheetNames[0]
        // 시트 데이터를 가져옴
        const worksheet = workbook.Sheets[sheetName]

        let jsonData
        // 시트 데이터를 JSON 형식으로 변환
        if (isEmptyDefval) {
          jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' })
        } else {
          jsonData = XLSX.utils.sheet_to_json(worksheet)
        }
        resolve(jsonData)
      } else {
        reject(new Error('Failed to read file'))
      }
    }

    // 파일 읽기 오류 시 호출되는 이벤트 핸들러
    reader.onerror = (error) => reject(error)
    // 파일을 ArrayBuffer로 읽기 시작
    reader.readAsArrayBuffer(file)
  })
}

/**
 * 엑셀 파일을 읽고 그룹 헤더와 필드를 포함한 JSON 데이터로 변환합니다.
 * @param file 엑셀 파일 (Blob 또는 File)
 * @returns {Promise<any[]>} 변환된 JSON 데이터
 */
export const readExcelWithGroupHeaders = async (file: File): Promise<any[]> => {
  try {
    // 파일을 ArrayBuffer로 읽기 시작
    const data = await file.arrayBuffer()
    // 엑셀 파일을 읽어 워크북 생성
    const workbook = XLSX.read(data, { type: 'array' })
    // 첫 번째 시트의 이름을 가져옴
    const sheetName = workbook.SheetNames[0]
    // 시트 데이터를 가져옴
    const sheet = workbook.Sheets[sheetName]

    if (!sheet) {
      throw new Error('엑셀 시트를 찾을 수 없습니다.')
    }

    // 시트 데이터를 JSON 형식으로 변환
    const rawData = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][]

    // 첫 번째 행을 그룹 헤더로 설정
    const groupHeaders = JSON.parse(JSON.stringify(rawData[0])).reduce((acc, cur) => {
      acc.push(cur === null ? acc[acc.length - 1] : cur) // 값이 null이라면(merge된 셀이라면) 이전 그룹 헤더로 설정
      return acc
    }, [])

    // 두 번째 행을 일반 헤더로 설정
    const fieldHeaders = rawData[1]

    // 나머지 행을 데이터로 설정
    const rows = rawData.slice(2)

    // 헤더 매핑
    const headers = fieldHeaders.map((fieldHeader, colIndex) => {
      const groupHeader = groupHeaders[colIndex]?.trim() || '' // 그룹 헤더가 없으면 빈 문자열로 처리
      const cleanFieldHeader = fieldHeader?.trim() || `Column${colIndex + 1}`

      if (groupHeader) {
        // 그룹 헤더가 존재하는 경우
        return `${groupHeader}^:${cleanFieldHeader}`
      } else {
        // 그룹 헤더가 없는 일반 헤더
        return cleanFieldHeader
      }
    })

    // 데이터를 헤더에 맞춰 변환
    const transformedData = rows.map((row) => {
      const result: any = {}

      headers.forEach((header, colIndex) => {
        const [group, field] = header.includes('^:')
          ? header.split('^:').map((h) => h.trim())
          : [null, header]

        if (group) {
          // 그룹 헤더가 있는 경우
          if (!result[group]) {
            result[group] = {}
          }
          result[group][field] = row[colIndex]
        } else {
          // 그룹 헤더가 없는 경우
          result[field] = row[colIndex]
        }
      })

      return result
    })

    return transformedData
  } catch (error) {
    console.error('엑셀 파일 읽기 중 오류 발생:', error)
    throw error
  }
}

/**
 * 객체에서 필수 필드만 남깁니다.
 * @template T - 객체의 타입
 * @param {T} item 모든 필드가 포함된 객체
 * @param {Array<keyof T>}requireFields 남길 필드들의 키 배열
 * @returns {Partial<T>} 필수 필드들만 포함된 객체
 */
export const mapData = <T>(item: T, requireFields: Array<keyof T>): Partial<T> => {
  const result: Partial<T> = { ...item }
  for (const key in result) {
    if (!requireFields.includes(key as keyof T)) {
      delete result[key as keyof T]
    }
  }
  return result
}
